import type { Article, Block, CategoryId, PageHelp } from "./types";
import { startArticles } from "./articles/start";
import { conceptArticles } from "./articles/concepts";
import { troubleshootingArticles } from "./articles/troubleshooting";
import { automationArticles } from "./articles/automation";
import { agentsArticles } from "./articles/agents";
import { ticketsArticles } from "./articles/tickets";
import { salesArticles } from "./articles/sales";
import { deliveryArticles } from "./articles/delivery";
import { insightsArticles } from "./articles/insights";
import { learningArticles } from "./articles/learning";
import { settingsArticles } from "./articles/settings";
import { homePageHelp } from "./pages/home";
import { salesPageHelp } from "./pages/sales";
import { deliveryPageHelp } from "./pages/delivery";
import { insightsPageHelp } from "./pages/insights";
import { learningPageHelp } from "./pages/learning";
import { settingsPageHelp } from "./pages/settings";

export type { Article, Block, CategoryId, PageHelp, ChecklistItem } from "./types";
export { CHECKLIST } from "./checklist";

export const CATEGORIES: { id: CategoryId; label: string; blurb: string }[] = [
  { id: "start", label: "Getting started", blurb: "New here? Begin with these." },
  { id: "sales", label: "Sales", blurb: "Pipeline, calls, setters and contracts." },
  { id: "delivery", label: "Delivery", blurb: "Onboard and serve customers." },
  { id: "insights", label: "Insights", blurb: "Reading your numbers." },
  { id: "learning", label: "Learning", blurb: "Playbook, drills and the wiki." },
  { id: "automation", label: "Automation", blurb: "GoHighLevel, research, audits and websites." },
  { id: "admin", label: "Settings and admin", blurb: "Team, permissions and configuration." },
  { id: "concepts", label: "Glossary", blurb: "What the terms and metrics mean." },
  { id: "troubleshooting", label: "Troubleshooting", blurb: "When something doesn't look right." },
];

export const ARTICLES: Article[] = [
  ...startArticles, ...salesArticles, ...deliveryArticles, ...ticketsArticles, ...insightsArticles, ...learningArticles,
  ...automationArticles, ...agentsArticles, ...settingsArticles, ...conceptArticles, ...troubleshootingArticles,
];

const BY_ID = new Map(ARTICLES.map((a) => [a.id, a]));
export const getArticle = (id: string): Article | undefined => BY_ID.get(id);
export const articlesIn = (cat: CategoryId): Article[] => ARTICLES.filter((a) => a.category === cat);

export const PAGE_HELP: Record<string, PageHelp> = {
  ...homePageHelp, ...salesPageHelp, ...deliveryPageHelp, ...insightsPageHelp, ...learningPageHelp, ...settingsPageHelp,
};

// Which help entry describes the page at this URL. Account pages are keyed by
// their section ("account-overview" or the module id); Settings by its tab.
export function helpKeyFor(pathname: string, search: string): string {
  const segs = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (segs.length === 0) return "home";
  if (segs[0] === "accounts") return !segs[2] || segs[2] === "overview" ? "account-overview" : segs[2];
  if (segs[0] === "settings") return `settings-${new URLSearchParams(search).get("tab") || "data"}`;
  if (segs[0] === "portal") return "portal";
  return segs[0];
}

export function getPageHelp(key: string): PageHelp | undefined {
  return PAGE_HELP[key] ?? (key.startsWith("settings-") ? PAGE_HELP.settings : undefined);
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

const plain = (s: string) => s.replace(/\*\*/g, "").replace(/`/g, "");

export function blockText(b: Block): string {
  switch (b.type) {
    case "p": case "h": return plain(b.text);
    case "callout": return plain(b.text);
    case "list": return b.items.map(plain).join(" ");
    case "steps": return b.items.map((i) => plain(`${i.title} ${i.body ?? ""}`)).join(" ");
    case "table": return [...b.head, ...b.rows.flat()].map(plain).join(" ");
    case "faq": return b.items.map((i) => plain(`${i.q} ${i.a}`)).join(" ");
    case "glossary": return b.items.map((i) => plain(`${i.term} ${i.def}`)).join(" ");
  }
}

type Indexed = { article: Article; title: string; summary: string; tags: string; body: string };
let INDEX: Indexed[] | null = null;
function index(): Indexed[] {
  if (!INDEX) {
    INDEX = ARTICLES.map((a) => ({
      article: a,
      title: a.title.toLowerCase(),
      summary: a.summary.toLowerCase(),
      tags: (a.tags ?? []).join(" ").toLowerCase(),
      body: a.body.map(blockText).join(" ").toLowerCase(),
    }));
  }
  return INDEX;
}

export type SearchHit = { article: Article; score: number; snippet: string };

export function searchHelp(query: string, limit = 25): SearchHit[] {
  const terms = query.toLowerCase().split(/\s+/).map((t) => t.trim()).filter((t) => t.length > 1);
  if (!terms.length) return [];
  const hits: SearchHit[] = [];
  for (const ix of index()) {
    let score = 0;
    let matchedAll = true;
    for (const t of terms) {
      let s = 0;
      if (ix.title.includes(t)) s += 12;
      if (ix.tags.includes(t)) s += 8;
      if (ix.summary.includes(t)) s += 5;
      if (ix.body.includes(t)) s += 2;
      if (s === 0) { matchedAll = false; break; }
      score += s;
    }
    if (!matchedAll) continue;
    if (ix.title.includes(terms.join(" "))) score += 10;
    let snippet = ix.article.summary;
    const at = ix.body.indexOf(terms[0]);
    if (at >= 0 && !ix.summary.includes(terms[0])) {
      const full = ix.article.body.map(blockText).join(" ");
      snippet = `${at > 40 ? "…" : ""}${full.slice(Math.max(0, at - 40), at + 120).trim()}…`;
    }
    hits.push({ article: ix.article, score, snippet });
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}
